import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { cn } from '../lib/utils';
import api from '../utils/api';
import { productPath } from '../utils/helpers';

// Aroma categories for mega menu - UPDATED to use new filter system
const aromasDonne = [
  { id: 'citrus', name: 'Cítrico', icon: '🍊', filter: 'olfactory_family=Cítrico' },
  { id: 'sweet', name: 'Dulce', icon: '🍯', filter: 'olfactory_family=Dulce' },
  { id: 'fresh', name: 'Fresco', icon: '❄️', filter: 'olfactory_family=Fresco' },
  { id: 'floral', name: 'Floral', icon: '🌸', filter: 'olfactory_family=Floral' },
  { id: 'woody', name: 'Madera', icon: '🌲', filter: 'olfactory_family=Madera' },
];

const aromasUomo = [
  { id: 'citrus', name: 'Cítrico', icon: '🍊', filter: 'olfactory_family=Cítrico' },
  { id: 'sweet', name: 'Dulce', icon: '🍯', filter: 'olfactory_family=Dulce' },
  { id: 'fresh', name: 'Fresco', icon: '❄️', filter: 'olfactory_family=Fresco' },
  { id: 'floral', name: 'Floral', icon: '🌸', filter: 'olfactory_family=Floral' },
  { id: 'woody', name: 'Madera', icon: '🌲', filter: 'olfactory_family=Madera' },
];

const Navbar = ({ promoOffset = 0, benefitsBarHeight = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { isAuthenticated, user } = useAuth();
  const { getItemCount } = useCart();
  const megaMenuRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const cartCount = getItemCount();

  // Close mega menu when route changes
  useEffect(() => {
    setShowMegaMenu(false);
    setIsMenuOpen(false);
    setShowSearch(false);
    setSearchQuery('');
    setSuggestions([]);
  }, [location.pathname]);

  // Close mega menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(event.target)) {
        setShowMegaMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
        setSuggestions([]);
      }
    };

    if (showMegaMenu || showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMegaMenu, showSearch]);

  // Focus search input when opening
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Fetch suggestions when query changes
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await api.get(`/products/suggest?q=${encodeURIComponent(searchQuery)}&limit=6`);
        setSuggestions(response.data.suggestions || []);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setMobileSubmenu(null);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false);
      setSearchQuery('');
      setSuggestions([]);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (suggestions.length === 0) {
      if (e.key === 'Escape') {
        setShowSearch(false);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const selected = suggestions[selectedIndex];
      navigate(productPath(selected));
      setShowSearch(false);
      setSearchQuery('');
      setSuggestions([]);
    } else if (e.key === 'Escape') {
      setShowSearch(false);
      setSuggestions([]);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  return (
    <header 
      className="fixed w-full max-w-full bg-brand-black sm:bg-brand-black/90 backdrop-blur-md z-40 overflow-visible"
      style={{ top: `${promoOffset + benefitsBarHeight}px` }}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-12 overflow-visible">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vng9vv17_logo-alphatica-blanco.png"
              alt="Alphatica"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Perfumes with Mega Menu */}
            <div className="relative" ref={megaMenuRef}>
              <button 
                onClick={() => setShowMegaMenu(!showMegaMenu)}
                className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-brand-cobalt transition-colors uppercase tracking-widest py-8"
                data-testid="products-nav-link"
              >
                Perfumes
                <ChevronDown size={14} className={cn("transition-transform duration-200", showMegaMenu && "rotate-180")} />
              </button>

              {/* Mega Menu */}
              {showMegaMenu && (
                <div 
                  className="absolute top-full z-[100] right-0 xl:right-auto xl:left-1/2 xl:-translate-x-1/2"
                >
                  <div className="bg-brand-black/[0.98] backdrop-blur-lg shadow-2xl border border-brand-cobalt/20 rounded-lg overflow-hidden w-[360px] xl:w-[800px]">
                    {/* Header */}
                    <div className="bg-brand-black border-b border-brand-cobalt/15 px-6 xl:px-8 py-3 xl:py-4">
                      <h3 className="text-xs uppercase tracking-widest text-brand-cobalt/70">Explora por tipo de aroma</h3>
                    </div>
                  
                  <div className="grid grid-cols-1 xl:grid-cols-2 max-h-[60vh] overflow-y-auto xl:overflow-y-visible">
                    {/* DONNE Column */}
                    <div className="p-4 xl:p-6 border-b xl:border-b-0 xl:border-r border-brand-cobalt/20">
                      <div className="flex items-center gap-2 mb-3 xl:mb-4 pb-2 border-b border-brand-magenta/30">
                        <span className="text-brand-magenta font-display text-base xl:text-lg">DONNE</span>
                        <span className="text-xs text-white/50">Para Ella</span>
                      </div>
                      <div className="space-y-0.5 xl:space-y-1">
                        {aromasDonne.map((aroma) => (
                          <Link
                            key={aroma.id}
                            to={`/products?gender=Mujer&${aroma.filter}`}
                            onClick={() => setShowMegaMenu(false)}
                            className="flex items-center gap-2 xl:gap-3 px-2 xl:px-3 py-1.5 xl:py-2 rounded-lg hover:bg-brand-magenta/10 transition-colors"
                          >
                            <span className="text-lg xl:text-xl hover:scale-110 transition-transform">{aroma.icon}</span>
                            <span className="text-xs xl:text-sm text-white/80 hover:text-brand-magenta transition-colors">{aroma.name}</span>
                            <ChevronRight size={14} className="ml-auto text-brand-magenta/30" />
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/products?gender=Mujer"
                        onClick={() => setShowMegaMenu(false)}
                        className="block mt-2 xl:mt-3 pt-2 xl:pt-3 border-t border-brand-magenta/20 text-xs text-brand-magenta hover:underline"
                      >
                        Ver todos para ella →
                      </Link>
                    </div>

                    {/* UOMO Column */}
                    <div className="p-4 xl:p-6">
                      <div className="flex items-center gap-2 mb-3 xl:mb-4 pb-2 border-b border-brand-cobalt/30">
                        <span className="text-brand-cobalt font-display text-base xl:text-lg">UOMO</span>
                        <span className="text-xs text-white/50">Para Él</span>
                      </div>
                      <div className="space-y-0.5 xl:space-y-1">
                        {aromasUomo.map((aroma) => (
                          <Link
                            key={aroma.id}
                            to={`/products?gender=Hombre&${aroma.filter}`}
                            onClick={() => setShowMegaMenu(false)}
                            className="flex items-center gap-2 xl:gap-3 px-2 xl:px-3 py-1.5 xl:py-2 rounded-lg hover:bg-brand-cobalt/10 transition-colors"
                          >
                            <span className="text-lg xl:text-xl hover:scale-110 transition-transform">{aroma.icon}</span>
                            <span className="text-xs xl:text-sm text-white/80 hover:text-brand-cobalt transition-colors">{aroma.name}</span>
                            <ChevronRight size={14} className="ml-auto text-brand-cobalt/30" />
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/products?gender=Hombre"
                        onClick={() => setShowMegaMenu(false)}
                        className="block mt-2 xl:mt-3 pt-2 xl:pt-3 border-t border-brand-cobalt/20 text-xs text-brand-cobalt hover:underline"
                      >
                        Ver todos para él →
                      </Link>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-brand-black/60 px-4 xl:px-8 py-3 xl:py-4 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 xl:gap-0 border-t border-brand-cobalt/10">
                    <div className="flex flex-wrap items-center gap-3 xl:gap-6">
                      <Link 
                        to="/products?perfume_type=Nicho" 
                        onClick={() => setShowMegaMenu(false)}
                        className="text-xs text-white/60 hover:text-brand-cobalt transition-colors"
                      >
                        💎 Nicho
                      </Link>
                      <Link 
                        to="/products?popularity=Más vendidos" 
                        onClick={() => setShowMegaMenu(false)}
                        className="text-xs text-white/60 hover:text-brand-cobalt transition-colors"
                      >
                        🔥 Más Vendidos
                      </Link>
                      {!isAuthenticated && (
                        <button
                          onClick={() => {
                            setShowMegaMenu(false);
                            window.dispatchEvent(new CustomEvent('openWelcomePopup'));
                          }}
                          className="text-xs text-brand-magenta hover:text-brand-magenta/80 transition-colors text-left"
                        >
                          🎁 Regístrate -10%
                        </button>
                      )}
                    </div>
                    <Link 
                      to="/products"
                      onClick={() => setShowMegaMenu(false)} 
                      className="text-xs text-brand-cobalt hover:underline font-medium"
                    >
                      Ver catálogo completo →
                    </Link>
                  </div>
                </div>
              </div>
              )}
            </div>

            <Link 
              to="/products?gender=Hombre" 
              className="text-sm font-medium text-white/80 hover:text-brand-cobalt transition-colors uppercase tracking-widest"
              data-testid="men-nav-link"
            >
              Hombre
            </Link>
            <Link 
              to="/products?gender=Mujer" 
              className="text-sm font-medium text-white/80 hover:text-brand-magenta transition-colors uppercase tracking-widest"
              data-testid="women-nav-link"
            >
              Mujer
            </Link>
            <Link 
              to="/products?popularity=Más vendidos" 
              className="text-sm font-medium text-white/80 hover:text-brand-cobalt transition-colors uppercase tracking-widest"
              data-testid="bestsellers-nav-link"
            >
              Más Vendidos
            </Link>
            {!isAuthenticated && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openWelcomePopup'))}
                className="text-sm font-medium text-white/80 hover:text-brand-magenta transition-colors uppercase tracking-widest"
                data-testid="register-nav-link"
              >
                Regístrate
              </button>
            )}
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-4 md:space-x-6">
            {/* Search Icon */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="text-white/70 hover:text-brand-cobalt transition-colors"
                data-testid="search-icon-btn"
                aria-label="Buscar"
              >
                <Search size={20} />
              </button>

              {/* Search Dropdown */}
              {showSearch && (
                <div className="fixed sm:absolute top-16 sm:top-full left-0 sm:left-auto right-0 sm:right-0 mx-2 sm:mx-0 sm:mt-4 w-auto sm:w-[400px] bg-brand-black/[0.98] backdrop-blur-lg border border-brand-cobalt/20 rounded-lg shadow-2xl overflow-hidden z-[100]">
                  <form onSubmit={handleSearchSubmit} className="p-3 border-b border-brand-cobalt/20">
                    <div className="relative flex items-center">
                      <Search className="absolute left-3 text-brand-cobalt/50" size={18} />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Código, marca, perfume..."
                        className="w-full pl-10 pr-10 py-2.5 text-base bg-brand-black/60 border border-brand-cobalt/20 rounded focus:outline-none focus:border-brand-cobalt text-white placeholder:text-white/40"
                        autoComplete="off"
                        data-testid="navbar-search-input"
                      />
                      {searchLoading && (
                        <div className="absolute right-10 w-4 h-4 border-2 border-brand-cobalt/30 border-t-brand-cobalt rounded-full animate-spin" />
                      )}
                      {searchQuery && !searchLoading && (
                        <button
                          type="button"
                          onClick={() => { setSearchQuery(''); setSuggestions([]); }}
                          className="absolute right-3 text-white/40 hover:text-brand-cobalt transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </form>

                  {/* Suggestions */}
                  {suggestions.length > 0 && (
                    <div className="max-h-[300px] overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <Link
                          key={suggestion.id}
                          to={productPath(suggestion)}
                          onClick={() => { setShowSearch(false); setSearchQuery(''); setSuggestions([]); }}
                          className={cn(
                            "flex items-center gap-3 px-4 py-2.5 transition-colors border-b border-brand-cobalt/10 last:border-b-0",
                            selectedIndex === index ? "bg-brand-cobalt/20" : "hover:bg-brand-cobalt/15"
                          )}
                        >
                          <div className="w-10 h-10 rounded bg-brand-black/60 overflow-hidden flex-shrink-0">
                            <img
                              src={suggestion.image || 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=100'}
                              alt={suggestion.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-white/60">{suggestion.product_code}</span>
                            <p className={cn(
                              "text-xs font-semibold truncate",
                              suggestion.gender === 'Hombre' ? 'text-brand-cobalt' : 'text-brand-magenta'
                            )}>
                              {suggestion.name}
                            </p>
                            <p className="text-[10px] text-white/50 truncate">{suggestion.brand}</p>
                          </div>
                          <div className="text-xs font-medium text-white">
                            {formatPrice(suggestion.price)}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* No results */}
                  {searchQuery.length >= 2 && suggestions.length === 0 && !searchLoading && (
                    <div className="p-4 text-center">
                      <p className="text-white/60 text-xs">No se encontraron productos</p>
                    </div>
                  )}

                  {/* View all button */}
                  {searchQuery.trim() && (
                    <button
                      onClick={handleSearchSubmit}
                      className="w-full px-4 py-2.5 text-xs text-brand-cobalt hover:bg-brand-cobalt/10 transition-colors border-t border-brand-cobalt/20 text-center"
                    >
                      Ver todos los resultados →
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <Link 
                to={user?.is_admin ? "/admin" : "/profile"} 
                className="text-white/70 hover:text-brand-cobalt transition-colors"
                data-testid="profile-icon-link"
              >
                <User size={20} />
              </Link>
            ) : (
              <Link 
                to="/login" 
                className="text-white/70 hover:text-brand-cobalt transition-colors"
                data-testid="login-icon-link"
              >
                <User size={20} />
              </Link>
            )}
            <Link 
              to="/cart" 
              className="relative text-white/70 hover:text-brand-cobalt transition-colors"
              data-testid="cart-icon-link"
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span 
                  className="absolute -top-2 -right-2 bg-brand-magenta text-white text-xs w-5 h-5 rounded-full flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white/70 hover:text-brand-cobalt transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Drawer lateral */}
        {isMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[100]">
          {/* Backdrop oscuro */}
          <div 
            className="absolute inset-0 bg-black/70"
            onClick={closeMobileMenu}
          />
          
          {/* Drawer Panel */}
          <div 
            className="absolute top-0 right-0 w-[85%] max-w-[320px] bg-brand-black border-l border-brand-cobalt/20 shadow-2xl flex flex-col overflow-hidden"
            style={{ height: '100vh' }}
          >
            {/* Header del Drawer */}
            <div className="flex items-center justify-between p-4 border-b border-brand-cobalt/20 bg-brand-black">
              <span className="text-lg font-display text-white">Menú</span>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-white/70 hover:text-brand-cobalt transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido del Drawer */}
            <div className="flex-1 overflow-y-auto bg-brand-black p-4" data-testid="mobile-menu">
              {mobileSubmenu === null ? (
                // Main menu
                <div className="space-y-2">
                  {/* Todos los perfumes */}
                  <Link
                    to="/products"
                    className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-white/90 hover:bg-brand-cobalt/10 rounded-xl transition-colors"
                    onClick={closeMobileMenu}
                  >
                    <span className="text-lg">🛍️</span>
                    <span className="uppercase tracking-wider">Todos los Perfumes</span>
                  </Link>
                  
                  <div className="h-px bg-brand-cobalt/10 my-3" />

                  {/* Sección DONNE */}
                  <button
                    onClick={() => setMobileSubmenu('donne')}
                    className="flex items-center justify-between w-full px-4 py-4 rounded-xl bg-gradient-to-r from-brand-magenta/10 to-transparent hover:from-brand-magenta/20 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">👗</span>
                      <div className="text-left">
                        <span className="block text-brand-magenta font-display text-lg">DONNE</span>
                        <span className="text-xs text-white/50">Fragancias para Ella</span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-brand-magenta/50" />
                  </button>
                  
                  {/* Sección UOMO */}
                  <button
                    onClick={() => setMobileSubmenu('uomo')}
                    className="flex items-center justify-between w-full px-4 py-4 rounded-xl bg-gradient-to-r from-brand-cobalt/10 to-transparent hover:from-brand-cobalt/20 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">👔</span>
                      <div className="text-left">
                        <span className="block text-brand-cobalt font-display text-lg">UOMO</span>
                        <span className="text-xs text-white/50">Fragancias para Él</span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-brand-cobalt/50" />
                  </button>

                  <div className="h-px bg-brand-cobalt/10 my-3" />

                  {/* Links rápidos */}
                  <Link
                    to="/products?popularity=Más vendidos"
                    className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-white/80 hover:bg-brand-cobalt/10 rounded-xl transition-colors"
                    onClick={closeMobileMenu}
                  >
                    <span className="text-lg">🔥</span>
                    <span className="uppercase tracking-wider">Más Vendidos</span>
                  </Link>
                  {!isAuthenticated && (
                    <button
                      className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-white/80 hover:bg-brand-magenta/10 rounded-xl transition-colors w-full text-left"
                      onClick={() => {
                        closeMobileMenu();
                        window.dispatchEvent(new CustomEvent('openWelcomePopup'));
                      }}
                    >
                      <span className="text-lg">🎁</span>
                      <span className="uppercase tracking-wider">Regístrate -10%</span>
                    </button>
                  )}
                  <Link
                    to="/products?perfume_type=Nicho"
                    className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-white/80 hover:bg-brand-cobalt/10 rounded-xl transition-colors"
                    onClick={closeMobileMenu}
                  >
                    <span className="text-lg">💎</span>
                    <span className="uppercase tracking-wider">Nicho</span>
                  </Link>
                </div>
              ) : (
                // Submenu (DONNE or UOMO)
                <div className="p-4">
                  {/* Back button */}
                  <button
                    onClick={() => setMobileSubmenu(null)}
                    className="flex items-center gap-2 px-2 py-2 text-sm text-white/60 hover:text-white mb-4"
                  >
                    <ChevronRight size={18} className="rotate-180" />
                    <span>Volver al menú</span>
                  </button>
                  
                  {/* Submenu header */}
                  <div className={`p-4 rounded-xl mb-4 ${
                    mobileSubmenu === 'donne' 
                      ? 'bg-gradient-to-r from-brand-magenta/20 to-brand-magenta/5' 
                      : 'bg-gradient-to-r from-brand-cobalt/20 to-brand-cobalt/5'
                  }`}>
                    <h3 className={`font-display text-2xl ${mobileSubmenu === 'donne' ? 'text-brand-magenta' : 'text-brand-cobalt'}`}>
                      {mobileSubmenu === 'donne' ? 'DONNE' : 'UOMO'}
                    </h3>
                    <p className="text-sm text-white/60 mt-1">
                      {mobileSubmenu === 'donne' ? 'Explora fragancias femeninas' : 'Explora fragancias masculinas'}
                    </p>
                  </div>
                  
                  {/* Título de sección */}
                  <p className="text-xs text-white/40 uppercase tracking-wider px-2 mb-3">
                    Familias Olfativas
                  </p>
                  
                  {/* Aroma links */}
                  <div className="space-y-1">
                    {(mobileSubmenu === 'donne' ? aromasDonne : aromasUomo).map((aroma) => (
                      <Link
                        key={aroma.id}
                        to={`/products?gender=${mobileSubmenu === 'donne' ? 'Mujer' : 'Hombre'}&${aroma.filter}`}
                        className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${
                          mobileSubmenu === 'donne' 
                            ? 'hover:bg-brand-magenta/10 active:bg-brand-magenta/20' 
                            : 'hover:bg-brand-cobalt/10 active:bg-brand-cobalt/20'
                        }`}
                        onClick={closeMobileMenu}
                      >
                        <span className="text-2xl">{aroma.icon}</span>
                        <span className="text-base text-white/90 font-medium">{aroma.name}</span>
                        <ChevronRight size={16} className={`ml-auto ${
                          mobileSubmenu === 'donne' ? 'text-brand-magenta/40' : 'text-brand-cobalt/40'
                        }`} />
                      </Link>
                    ))}
                  </div>
                  
                  {/* View all button */}
                  <Link
                    to={`/products?gender=${mobileSubmenu === 'donne' ? 'Mujer' : 'Hombre'}`}
                    className={`flex items-center justify-center gap-2 mt-6 py-4 text-sm font-medium border-2 rounded-xl transition-all ${
                      mobileSubmenu === 'donne' 
                        ? 'text-brand-magenta border-brand-magenta hover:bg-brand-magenta hover:text-brand-black' 
                        : 'text-brand-cobalt border-brand-cobalt hover:bg-brand-cobalt hover:text-brand-black'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    Ver todos {mobileSubmenu === 'donne' ? 'para Ella' : 'para Él'}
                    <ChevronRight size={16} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
